import React from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, Rocket, Target, LineChart, CheckCircle2, ArrowRight, Calendar, Shield, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSound } from '../hooks/useSound';

const executionPlans = [
  {
    title: 'AI-Driven Business Execution',
    icon: BrainCircuit,
    description: 'Transform your business with AI-powered strategies and real-time optimization',
    features: [
      'Custom AI execution roadmap',
      'Real-time performance tracking',
      'Strategic implementation support',
      'ROI-focused optimization'
    ]
  },
  {
    title: 'Growth Acceleration',
    icon: Rocket,
    description: 'Scale your business faster with data-backed strategies and AI insights',
    features: [
      'Market opportunity analysis',
      'Competitive positioning',
      'Growth strategy development',
      'Performance benchmarking'
    ]
  },
  {
    title: 'Operational Excellence',
    icon: Target,
    description: 'Optimize your operations for maximum efficiency and profitability',
    features: [
      'Process optimization',
      'Resource allocation',
      'Cost reduction strategies',
      'Quality improvement'
    ]
  }
];

const benefits = [
  {
    icon: LineChart,
    title: 'Average 10X ROI',
    description: 'Our clients see significant returns through improved efficiency and growth'
  },
  {
    icon: Shield,
    title: 'Custom AI Solutions',
    description: 'Tailored execution plans designed specifically for your business needs'
  },
  {
    icon: Sparkles,
    title: 'Expert Implementation',
    description: 'Dedicated support to ensure successful execution of your AI strategies'
  }
];

const steps = [
  {
    number: '01',
    title: 'Book a Strategy Call',
    description: 'Discuss your goals and AI execution needs',
    icon: Calendar
  },
  {
    number: '02',
    title: 'Get Your Custom Plan',
    description: 'Receive a tailored AI execution roadmap',
    icon: Target
  },
  {
    number: '03',
    title: 'Start Growing',
    description: 'Execute your plan with our expert support',
    icon: Rocket
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Pricing = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Custom AI Execution Plans Built for Your Business
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Unlock scalable growth with AI-driven strategies tailored to your specific industry and business needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {executionPlans.map((plan, index) => (
            <motion.div
              key={plan.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
            >
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.8, ...springConfig }}
                className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.8, ...springConfig }}
                  className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center"
                >
                  <plan.icon className="w-6 h-6 text-amber-400" />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-2">{plan.title}</h3>
                <p className="text-gray-400 mb-4">{plan.description}</p>
                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-amber-400" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1, ...springConfig }}
          className="text-center p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-2">Your Investment = Scalable Growth</h2>
          <p className="text-gray-400 mb-8">
            ProfitIQ.AI delivers an average 10X ROI—pricing is structured around your business needs and growth objectives
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + (index * 0.2), duration: 1, ...springConfig }}
                className="space-y-2"
              >
                <div className="text-2xl font-bold text-amber-400">{step.number}</div>
                <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
              className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.8, ...springConfig }}
                className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center"
              >
                <benefit.icon className="w-6 h-6 text-amber-400" />
              </motion.div>
              <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
              <p className="text-gray-400">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1, ...springConfig }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Transform Your Business?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Get your custom AI execution plan and start growing your business today
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
            onMouseEnter={playHover}
            onClick={() => {
              playClick();
              navigate('/consultation');
            }}
            className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center gap-2 mx-auto"
          >
            Get Your AI Execution Quote
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};